import {
  Breadcrumbs,
  ChangeCourseModalLayout,
  CourseDescriptionSection,
  CourseStructure,
  FullscreenClipLoader,
  Icon,
  LearningMapCard,
  SkipModuleModalLayout,
} from '@components';
import { routes } from '@constants';
import {
  useNavigateWithOfflineMode,
  useScreenSize,
  useScrollToTop,
} from '@hooks';
import OnboardingCover from '@pngs/AboutUsPage/ambassador4.png';
import Aim from '@pngs/LearningMapPage/Aim.png';
import Net from '@pngs/LearningMapPage/Net.png';
import Structure from '@pngs/LearningMapPage/Structure.png';
import { courseApi, courseSlice, userApi } from '@store';
import { Button, Flex, Image, theme, Typography } from '@styles';
import { ReactComponent as ChangeIcon } from '@svgs/change.svg';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import Buttons from './components/Buttons';
import { learningMapPageLayoutByVariant } from './LearningMapPage.layoutProps';

const LearningMapPage = () => {
  const { navigate } = useNavigateWithOfflineMode();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const [isChangeCourseModalVisible, setIsChangeCourseModalVisible] =
    useState(false);
  const [isSkipModalVisible, setIsSkipModalVisible] = useState(false);
  const [moduleToSkip, setModuleToSkip] = useState(null);

  const containerRef = useRef(null);

  const { device, isTabletLG, isTabletSM } = useScreenSize();
  const layout = learningMapPageLayoutByVariant[device || 'desktop'];

  const currentProgram = useSelector(
    (state) => state.courseSlice.currentProgram
  );
  const isOnboardingPassed = useSelector(
    (state) => state.authSlice.isOnboardingPassed
  );

  const { courses, refetch: refetchCourses } = courseApi.useGetCoursesQuery(
    undefined,
    {
      selectFromResult: ({ data }) => ({
        courses: data?.data?.courses,
      }),
    }
  );
  const { course, refetch: refetchCourse } = courseApi.useGetCourseByIdQuery(
    { id: currentProgram },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data }) => ({
        course: data?.data,
      }),
    }
  );
  const { learningMap, refetch: refetchLearningMap } =
    userApi.useGetCourseProgressByIdQuery(
      { id: currentProgram },
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ data }) => ({
          learningMap: data?.data?.modules,
        }),
      }
    );

  useEffect(() => {
    refetchCourses();
    refetchCourse({ id: currentProgram });
    refetchLearningMap({ id: currentProgram });
  }, [i18n.language]);

  useEffect(() => {
    if (course) {
      dispatch(courseSlice.actions.setCourseById(course));
    }
  }, [course]);

  const handleGoToOnboarding = () => () => {
    navigate(routes.onboarding);
  };

  const handleGoToCertification = () => () => {
    navigate(`/courses/${course?.id}/certification`, {
      state: { id: course?.test_id },
    });
  };

  const handleChangeProgram = () => {
    setIsChangeCourseModalVisible(true);
  };

  const setCourse = (id) => {
    dispatch(courseSlice.actions.setCurrentProgram(id));
    setIsChangeCourseModalVisible(false);
  };

  const openSkipModal =
    ({ courseId, id, isCompleted }) =>
    () => {
      setModuleToSkip({ courseId, id });
      if (!isCompleted) {
        setIsSkipModalVisible(true);
      } else {
        navigate(`/courses/${courseId}/${id}`, {
          state: { finalTest: true },
        });
      }
    };

  const handleSkipModule = () => {
    navigate(`/courses/${moduleToSkip?.courseId}/${moduleToSkip?.id}`, {
      state: { finalTest: true },
    });
  };

  const scrollMap = (forward) => () => {
    if (containerRef?.current) {
      if (forward) containerRef.current.scrollLeft += 348;
      else containerRef.current.scrollLeft -= 348;
    }
  };

  useScrollToTop();

  const isFinalTestHighlightedOnMap = learningMap?.every(
    (module) => module.is_completed
  );

  const isOnboardingHighlightedOnMap =
    !learningMap?.some((module) => module.is_highlighted_on_map) &&
    !isOnboardingPassed &&
    !isFinalTestHighlightedOnMap;

  if (!learningMap || !course) {
    return <FullscreenClipLoader />;
  }

  return (
    <>
      <ChangeCourseModalLayout
        courses={courses}
        isVisible={isChangeCourseModalVisible}
        setCurrentCourse={setCourse}
        setIsVisible={setIsChangeCourseModalVisible}
      />
      <SkipModuleModalLayout
        handleSkipModule={handleSkipModule}
        isVisible={isSkipModalVisible}
        setIsVisible={setIsSkipModalVisible}
      />
      <Flex flexDirection="column">
        <Breadcrumbs containerProps={{ px: layout?.px }} />
        <Flex
          backgroundImage={Net}
          backgroundPosition="left"
          backgroundSize="contain"
          flexDirection="column"
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            pb={10}
            pt={isTabletSM ? 0 : 15}
            px={layout?.px}
          >
            <Flex gap={8} grow={0}>
              <Typography
                color={theme.colors.white[100]}
                variant={
                  isTabletSM ? 'MS-regular-s16-lh25' : 'MS-regular-s26-lh40'
                }
              >
                {i18n.t('LearningMapPage.LearningMap')}
              </Typography>
              <Typography
                color={theme.colors.schoolBusYellow[100]}
                variant={
                  isTabletSM ? 'MS-regular-s16-lh25' : 'MS-regular-s26-lh40'
                }
              >
                {course?.title?.toUpperCase()}
              </Typography>
            </Flex>
            <Button
              border={0}
              onClick={handleChangeProgram}
              variant="quaternary"
            >
              {isTabletSM ? (
                <Icon height={32} SVG={ChangeIcon} width={32} />
              ) : (
                <Typography
                  color={theme.colors.schoolBusYellow[100]}
                  variant="MS-semibold-s18-lh19"
                >
                  {i18n.t(`LearningMapPage.ChangeProgram`)}
                </Typography>
              )}
            </Button>
          </Flex>
          <Flex justifyContent="flex-end" px={layout?.px}>
            <Buttons next={scrollMap(true)} previous={scrollMap(false)} />
          </Flex>
          <Flex
            ref={containerRef}
            className="scrollbar-hidden"
            flexWrap="no-wrap"
            overflowX="scroll"
            pb={80}
            pt={isTabletSM ? 10 : 30}
            style={{ scrollBehavior: 'smooth' }}
          >
            <LearningMapCard
              containerRef={containerRef}
              isFirst
              module={{
                id: 0,
                image: OnboardingCover,
                is_completed: isOnboardingPassed,
                is_highlighted_on_map: isOnboardingHighlightedOnMap,
                lessons: [
                  {
                    id: 0,
                    is_available: true,
                    is_completed: isOnboardingPassed,
                    is_highlighted_on_map: isOnboardingHighlightedOnMap,
                    title: i18n.t('LearningMapPage.Buttons.Onboarding'),
                  },
                ],
                title: i18n.t('CoursesPage.Onboarding'),
              }}
              onClick={handleGoToOnboarding}
              padding={layout.px}
            />
            {learningMap?.map((module) => (
              <LearningMapCard
                containerRef={containerRef}
                courseId={currentProgram}
                module={module}
                openSkipModal={openSkipModal}
                padding={layout.px}
              />
            ))}
            <LearningMapCard
              containerRef={containerRef}
              isLast
              module={{
                id: 0,
                image: `https://geneva-learning-platform-resources.s3.eu-north-1.amazonaws.com/images/modules/certification-${course?.id}.png`,
                is_completed: course?.is_passed,
                is_highlighted_on_map: isFinalTestHighlightedOnMap,
                lessons: [
                  {
                    id: 0,
                    is_available: true,
                    is_completed: course?.is_passed,
                    is_highlighted_on_map: isFinalTestHighlightedOnMap,
                    title: i18n.t('LearningMapPage.Buttons.Certification'),
                  },
                ],
                title: i18n.t('Certification.Title'),
              }}
              onClick={handleGoToCertification}
              padding={layout.px}
            />
          </Flex>
        </Flex>
        {isTabletSM ? (
          <Flex
            bg={theme.colors.backgroundLight}
            flexDirection="column"
            gap={8}
            pt={56}
            px={layout?.px}
          >
            <Typography
              color={theme.colors.schoolBusYellow[100]}
              variant="MS-regular-s24-lh28"
            >
              {i18n.t('LearningMapPage.Aim.Title')}
            </Typography>
            <Typography
              color={theme.colors.white[100]}
              variant="MS-regular-s18-lh28"
            >
              {course?.learning_outcome}
            </Typography>
            <AimImage height="20%" src={Aim} />
          </Flex>
        ) : (
          <AimContainer
            backgroundImage={Aim}
            backgroundPosition="right"
            backgroundRepeat="no-repeat"
            backgroundSize="50%"
            bg={theme.colors.backgroundLight}
            flexDirection="column"
            gap={8}
            px={layout?.px}
            py={isTabletLG ? 56 : 80}
          >
            <Typography
              color={theme.colors.schoolBusYellow[100]}
              variant="MS-regular-s26-lh40"
            >
              {i18n.t('LearningMapPage.Aim.Title')}
            </Typography>
            <Typography
              color={theme.colors.white[100]}
              variant="MS-regular-s20-lh31"
              width="50%"
            >
              {course?.learning_outcome}
            </Typography>
          </AimContainer>
        )}
        <CourseDescriptionSection
          attitude={course?.attitude}
          containerProps={{ px: layout.px }}
          knowledge={course?.knowledge}
        />
        <StructureContainer
          backgroundImage={Structure}
          backgroundPosition="top 30px left 0"
          backgroundRepeat="no-repeat"
          backgroundSize="contain"
          flexDirection="column"
          gap={48}
          pb={100}
          pt={50}
          px={layout.px}
        >
          <Typography
            color={theme.colors.white[100]}
            variant="MS-regular-s26-lh40"
          >
            {i18n.t('LearningMapPage.StructureTitle')}
          </Typography>
          <CourseStructure modules={course?.modules} />
        </StructureContainer>
      </Flex>
    </>
  );
};

const AimContainer = styled(Flex)`
  background-blend-mode: soft-light;
`;

const AimImage = styled(Image)`
  mix-blend-mode: soft-light;
`;

const StructureContainer = styled(Flex)`
  background-blend-mode: overlay;
`;

export default LearningMapPage;
